import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTour } from '../../store/TourContext';
import { useSaaSContext } from '../../store/SaaSContext';
import { TOUR_STEPS } from './tourSteps';
import { 
  Sparkles, 
  ChevronRight, 
  ChevronLeft, 
  X, 
  CheckCircle2, 
  Play, 
  Compass, 
  Zap, 
  ListOrdered
} from 'lucide-react';

interface RectBounds {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

export const ProductTourModal: React.FC = () => {
  const {
    isTourOpen,
    currentStepIndex,
    totalSteps,
    currentStep,
    closeTour,
    nextStep,
    prevStep,
    goToStep,
    runStepAction,
    hasAction,
    showWelcomePrompt,
    dismissWelcomePrompt,
    startTour
  } = useTour();

  const { theme } = useSaaSContext();
  const isLight = theme === 'light';

  const [targetRect, setTargetRect] = useState<RectBounds | null>(null);
  const [showStepJumper, setShowStepJumper] = useState(false);
  const [measuredCardHeight, setMeasuredCardHeight] = useState<number>(440);
  const cardRef = useRef<HTMLDivElement>(null);

  // Measure card DOM height dynamically on resize / step change
  useEffect(() => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    if (rect.height > 100) {
      setMeasuredCardHeight(rect.height);
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.contentRect.height;
        if (h > 100 && Math.abs(h - measuredCardHeight) > 8) {
          setMeasuredCardHeight(h);
        }
      }
    });
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [currentStepIndex, isTourOpen]);

  // Keyboard navigation for Tour Guide (ArrowRight / Enter for Next, ArrowLeft for Back, Esc to exit)
  useEffect(() => {
    if (!isTourOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeTour();
      } else if (e.key === 'ArrowRight' || (e.key === 'Enter' && !e.shiftKey)) {
        const targetTag = (e.target as HTMLElement)?.tagName?.toLowerCase();
        if (targetTag !== 'input' && targetTag !== 'textarea') {
          nextStep();
        }
      } else if (e.key === 'ArrowLeft') {
        const targetTag = (e.target as HTMLElement)?.tagName?.toLowerCase();
        if (targetTag !== 'input' && targetTag !== 'textarea') {
          prevStep();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTourOpen, nextStep, prevStep, closeTour]);

  // Measure target element position
  const updateTargetBounds = useCallback(() => {
    if (!isTourOpen) return;

    if (currentStep.targetSelector === 'center') {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(currentStep.targetSelector);
    if (el) {
      const rect = el.getBoundingClientRect();
      const isInViewport = 
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth);

      if (!isInViewport) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }

      setTargetRect(prev => {
        if (
          prev &&
          prev.width === rect.width &&
          prev.height === rect.height &&
          Math.abs(prev.top - rect.top) < 0.5 &&
          Math.abs(prev.left - rect.left) < 0.5
        ) {
          return prev;
        }
        return {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          bottom: rect.bottom,
          right: rect.right
        };
      });
    } else {
      setTargetRect(prev => prev === null ? null : null);
    }
  }, [isTourOpen, currentStep]);

  // Update bounds on step change, resize, scroll
  useEffect(() => {
    if (!isTourOpen) return;

    const timer = setTimeout(updateTargetBounds, 160);

    const handleResizeOrScroll = () => {
      updateTargetBounds();
    };

    window.addEventListener('resize', handleResizeOrScroll);
    window.addEventListener('scroll', handleResizeOrScroll, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResizeOrScroll);
      window.removeEventListener('scroll', handleResizeOrScroll, true);
    };
  }, [isTourOpen, currentStepIndex, updateTargetBounds]);

  // Calculate tooltip card position
  const calculateTooltipPosition = () => {
    if (!targetRect || currentStep.targetSelector === 'center') {
      return {
        isCentered: true,
        style: {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '560px',
          width: '92vw'
        }
      };
    }

    const margin = 16;
    const cardWidth = Math.min(480, window.innerWidth - 32);
    const actualHeight = measuredCardHeight || 440;

    let top = 0;
    let left = 0;
    let placement = currentStep.placement || 'bottom';

    const isTargetHuge = targetRect.height > (window.innerHeight * 0.55) || targetRect.width > (window.innerWidth * 0.8);

    if (isTargetHuge) {
      top = window.innerHeight - actualHeight - 20;
      left = (window.innerWidth - cardWidth) / 2;
      return {
        isCentered: false,
        style: {
          top: `${Math.max(16, top)}px`,
          left: `${Math.max(16, left)}px`,
          width: `${cardWidth}px`,
          maxWidth: '94vw',
          maxHeight: 'calc(100vh - 32px)'
        },
        placement: 'bottom'
      };
    }

    const spaceBelow = window.innerHeight - targetRect.bottom;
    const spaceAbove = targetRect.top;
    const spaceRight = window.innerWidth - targetRect.right;
    const spaceLeft = targetRect.left;

    if (placement === 'bottom' && spaceBelow < actualHeight + margin && spaceAbove > actualHeight + margin) {
      placement = 'top';
    } else if (placement === 'top' && spaceAbove < actualHeight + margin && spaceBelow > actualHeight + margin) {
      placement = 'bottom';
    } else if (placement === 'right' && spaceRight < cardWidth + margin && spaceLeft > cardWidth + margin) {
      placement = 'left';
    } else if (placement === 'left' && spaceLeft < cardWidth + margin && spaceRight > cardWidth + margin) {
      placement = 'right';
    }

    switch (placement) {
      case 'bottom':
        top = targetRect.bottom + margin;
        left = targetRect.left + (targetRect.width / 2) - (cardWidth / 2);
        break;
      case 'top':
        top = targetRect.top - actualHeight - margin;
        left = targetRect.left + (targetRect.width / 2) - (cardWidth / 2);
        break;
      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (actualHeight / 2);
        left = targetRect.right + margin;
        break;
      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (actualHeight / 2);
        left = targetRect.left - cardWidth - margin;
        break;
      default:
        top = targetRect.bottom + margin;
        left = targetRect.left;
    }

    left = Math.max(16, Math.min(left, window.innerWidth - cardWidth - 16));
    top = Math.max(16, Math.min(top, window.innerHeight - actualHeight - 16));

    return {
      isCentered: false,
      style: {
        top: `${top}px`,
        left: `${left}px`,
        width: `${cardWidth}px`,
        maxWidth: '94vw',
        maxHeight: 'calc(100vh - 32px)'
      },
      placement
    };
  };

  const { isCentered, style: tooltipStyle } = calculateTooltipPosition();

  // Category Badge Styles (Adapted for supreme readability in both Light & Dark modes)
  const getCategoryColorStyles = (color: string) => {
    if (isLight) {
      switch (color) {
        case 'blue':
          return 'bg-blue-50 text-blue-700 border-blue-200 ring-blue-300/50';
        case 'emerald':
          return 'bg-emerald-50 text-emerald-700 border-emerald-200 ring-emerald-300/50';
        case 'amber':
          return 'bg-amber-50 text-amber-800 border-amber-200 ring-amber-300/50';
        case 'purple':
          return 'bg-purple-50 text-purple-700 border-purple-200 ring-purple-300/50';
        case 'rose':
          return 'bg-rose-50 text-rose-700 border-rose-200 ring-rose-300/50';
        case 'cyan':
          return 'bg-cyan-50 text-cyan-700 border-cyan-200 ring-cyan-300/50';
        default:
          return 'bg-indigo-50 text-indigo-700 border-indigo-200 ring-indigo-300/50';
      }
    }

    // Dark Mode
    switch (color) {
      case 'blue':
        return 'bg-blue-950/80 text-blue-300 border-blue-800/80 ring-blue-500/30';
      case 'emerald':
        return 'bg-emerald-950/80 text-emerald-300 border-emerald-800/80 ring-emerald-500/30';
      case 'amber':
        return 'bg-amber-950/80 text-amber-300 border-amber-800/80 ring-amber-500/30';
      case 'purple':
        return 'bg-purple-950/80 text-purple-300 border-purple-800/80 ring-purple-500/30';
      case 'rose':
        return 'bg-rose-950/80 text-rose-300 border-rose-800/80 ring-rose-500/30';
      case 'cyan':
        return 'bg-cyan-950/80 text-cyan-300 border-cyan-800/80 ring-cyan-500/30';
      default:
        return 'bg-indigo-950/80 text-indigo-300 border-indigo-800/80 ring-indigo-500/30';
    }
  };

  return (
    <>
      {/* Floating Welcome Invitation Banner (for first time visitors) */}
      <AnimatePresence>
        {showWelcomePrompt && !isTourOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.9 }}
            className={`fixed bottom-6 right-6 z-[9990] max-w-sm rounded-2xl shadow-2xl p-4.5 flex flex-col gap-3 backdrop-blur-xl border transition-all ${
              isLight 
                ? 'bg-white/95 border-indigo-200 shadow-indigo-100/50 text-slate-800' 
                : 'bg-zinc-900/95 border-indigo-500/50 shadow-2xl text-zinc-100'
            }`}
            id="tour-welcome-prompt"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 to-purple-600 flex items-center justify-center text-white shrink-0 shadow-md">
                  <Compass className="w-5 h-5 animate-spin-slow" />
                </div>
                <div>
                  <h4 className={`font-bold text-sm ${isLight ? 'text-slate-900' : 'text-white'}`}>
                    New to Variable Sticker Studio?
                  </h4>
                  <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`}>
                    Take a 2-minute interactive walkthrough
                  </p>
                </div>
              </div>
              <button
                onClick={dismissWelcomePrompt}
                className={`p-1 rounded-md transition-colors ${
                  isLight ? 'text-slate-400 hover:text-slate-700 hover:bg-slate-100' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                }`}
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-300'}`}>
              Learn how to import datasets, design variable labels, configure press imposition, calculate ROI, and export print-ready PDFs.
            </p>

            <div className={`flex items-center gap-2 pt-2 border-t ${isLight ? 'border-slate-100' : 'border-zinc-800'}`}>
              <button
                onClick={() => startTour(0)}
                className="flex-1 py-2 px-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 !text-white rounded-lg text-xs font-bold transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer active:scale-98"
              >
                <Play className="w-3.5 h-3.5 fill-current text-white" /> <span className="!text-white font-bold">Start Guided Tour</span>
              </button>
              <button
                onClick={dismissWelcomePrompt}
                className={`py-2 px-3 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                  isLight 
                    ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200' 
                    : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700'
                }`}
              >
                Later
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interactive Tour Overlay & Spotlight Cutout */}
      <AnimatePresence>
        {isTourOpen && (
          <div className="fixed inset-0 z-[9999] pointer-events-none select-none overflow-hidden">
            {/* Dimming Backdrop with SVG Mask */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-auto transition-all duration-300"
              style={{ width: '100vw', height: '100vh' }}
              onClick={closeTour}
            >
              <defs>
                <mask id="tour-spotlight-mask">
                  <rect x="0" y="0" width="100%" height="100%" fill="white" />
                  {targetRect && (
                    <rect
                      x={Math.max(0, targetRect.left - 6)}
                      y={Math.max(0, targetRect.top - 6)}
                      width={targetRect.width + 12}
                      height={targetRect.height + 12}
                      rx="10"
                      ry="10"
                      fill="black"
                    />
                  )}
                </mask>
              </defs>
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill={isLight ? 'rgba(15, 23, 42, 0.65)' : 'rgba(9, 9, 11, 0.82)'}
                mask="url(#tour-spotlight-mask)"
              />
            </svg>

            {/* Glowing Focus Ring around Target Element */}
            {targetRect && (
              <div
                className="absolute pointer-events-none transition-all duration-200 ease-out"
                style={{
                  top: `${Math.max(0, targetRect.top - 6)}px`,
                  left: `${Math.max(0, targetRect.left - 6)}px`,
                  width: `${targetRect.width + 12}px`,
                  height: `${targetRect.height + 12}px`,
                  borderRadius: '10px',
                  boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.95), 0 0 24px rgba(99, 102, 241, 0.5)',
                  border: '2px solid rgba(255, 255, 255, 0.85)'
                }}
              >
                {/* Radar Pulse Beacon */}
                <div className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-indigo-500 animate-ping opacity-75" />
                <div className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-indigo-600 border-2 border-white shadow-sm" />
              </div>
            )}

            {/* Interactive Tooltip Card */}
            <motion.div
              ref={cardRef}
              initial={{ opacity: 0, scale: 0.92, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 15 }}
              transition={{ duration: 0.22 }}
              style={tooltipStyle}
              className={`absolute pointer-events-auto rounded-2xl shadow-2xl backdrop-blur-2xl flex flex-col z-[10000] overflow-hidden transition-colors border ${
                isLight
                  ? 'bg-white/98 text-slate-900 border-slate-200 shadow-xl'
                  : 'bg-zinc-900/98 text-zinc-100 border-zinc-700/80 shadow-2xl'
              } ${isCentered ? 'ring-3 ring-indigo-500/40' : ''}`}
            >
              {/* Card Header Bar */}
              <div className={`px-5 py-3.5 border-b flex items-center justify-between gap-3 shrink-0 ${
                isLight ? 'bg-slate-50/90 border-slate-200' : 'bg-zinc-950/80 border-zinc-800'
              }`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex items-center gap-1.5 mr-1">
                    <button 
                      onClick={closeTour} 
                      className="w-3.5 h-3.5 rounded-full bg-red-500 hover:bg-red-600 transition-colors cursor-pointer" 
                      title="Close Tour (Esc)" 
                    ></button>
                    <div className="w-3.5 h-3.5 rounded-full bg-yellow-500/50" />
                    <div className="w-3.5 h-3.5 rounded-full bg-green-500/50" />
                  </div>
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ring-1 flex items-center gap-1 shrink-0 ${getCategoryColorStyles(currentStep.categoryColor)}`}>
                    <Sparkles className="w-3 h-3" />
                    {currentStep.category}
                  </span>
                  <span className={`text-xs font-mono font-semibold truncate ${
                    isLight ? 'text-slate-500' : 'text-zinc-400'
                  }`}>
                    Step {currentStepIndex + 1} of {totalSteps}
                  </span>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  {/* Step Jumper Dropdown Toggle */}
                  <button
                    onClick={() => setShowStepJumper(!showStepJumper)}
                    className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                      isLight 
                        ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-200' 
                        : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                    }`}
                    title="Jump to a specific step"
                  >
                    <ListOrdered className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Step Jumper Quick List Overlay */}
              <AnimatePresence>
                {showStepJumper && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className={`border-b p-3 max-h-48 overflow-y-auto custom-scrollbar flex flex-col gap-1 shrink-0 ${
                      isLight ? 'bg-slate-50 border-slate-200' : 'bg-zinc-950 border-zinc-800'
                    }`}
                  >
                    <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 px-1 ${
                      isLight ? 'text-slate-400' : 'text-zinc-500'
                    }`}>
                      Quick Step Jumper
                    </div>
                    {TOUR_STEPS.map((s, idx) => (
                      <button
                        key={s.id}
                        onClick={() => {
                          goToStep(idx);
                          setShowStepJumper(false);
                        }}
                        className={`text-left px-2.5 py-1.5 rounded-md text-xs flex items-center justify-between transition-colors ${
                          idx === currentStepIndex
                            ? isLight 
                              ? 'bg-indigo-100 text-indigo-900 font-bold border border-indigo-300'
                              : 'bg-indigo-600/30 text-indigo-300 font-bold border border-indigo-500/40'
                            : isLight
                              ? 'text-slate-700 hover:bg-slate-200 hover:text-slate-900'
                              : 'text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200'
                        }`}
                      >
                        <span className="truncate">
                          {idx + 1}. {s.title}
                        </span>
                        <span className="text-[10px] opacity-75 font-mono shrink-0 ml-2">{s.tab}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Card Body - Scrollable */}
              <div className="p-4 sm:p-5 flex flex-col gap-3.5 overflow-y-auto custom-scrollbar flex-1 min-h-0">
                <div>
                  <h3 className={`text-base sm:text-lg font-bold flex items-center gap-2 ${
                    isLight ? 'text-slate-900' : 'text-white'
                  }`}>
                    {currentStep.title}
                  </h3>
                  <p className={`text-xs sm:text-sm mt-1 leading-relaxed ${
                    isLight ? 'text-slate-600' : 'text-zinc-300'
                  }`}>
                    {currentStep.description}
                  </p>
                </div>

                {/* Key Highlights Checklist */}
                {currentStep.keyHighlights && currentStep.keyHighlights.length > 0 && (
                  <div className={`rounded-xl p-3 border flex flex-col gap-2 ${
                    isLight ? 'bg-slate-100/80 border-slate-200' : 'bg-zinc-950/80 border-zinc-800/90'
                  }`}>
                    <span className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                      isLight ? 'text-slate-600' : 'text-zinc-400'
                    }`}>
                      <CheckCircle2 className={`w-3.5 h-3.5 ${isLight ? 'text-emerald-600' : 'text-emerald-400'}`} />
                      Key Features &amp; Capabilities:
                    </span>
                    <ul className="space-y-1.5">
                      {currentStep.keyHighlights.map((highlight, i) => (
                        <li key={i} className={`text-xs flex items-start gap-2 ${
                          isLight ? 'text-slate-700' : 'text-zinc-300'
                        }`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0 mt-1.5" />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Interactive Action Trigger Button */}
                {hasAction && currentStep.actionLabel && (
                  <div className="pt-1">
                    <button
                      onClick={runStepAction}
                      className={`w-full py-2.5 px-3.5 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer group border ${
                        isLight
                          ? 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200 shadow-indigo-100'
                          : 'bg-indigo-950/40 hover:bg-indigo-900/50 text-indigo-200 border-indigo-500/40'
                      }`}
                    >
                      <Zap className={`w-4 h-4 group-hover:scale-110 transition-transform ${
                        isLight ? 'text-indigo-600' : 'text-indigo-400'
                      }`} />
                      <span>{currentStep.actionLabel}</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Progress Dots Bar */}
              <div className={`px-5 pt-1.5 pb-2 flex items-center justify-between gap-1 border-t shrink-0 ${
                isLight ? 'bg-slate-50/70 border-slate-200' : 'bg-zinc-950/70 border-zinc-800/80'
              }`}>
                <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar py-1">
                  {TOUR_STEPS.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goToStep(i)}
                      className={`h-1.5 rounded-full transition-all cursor-pointer ${
                        i === currentStepIndex
                          ? 'w-6 bg-indigo-600 shadow-xs'
                          : i < currentStepIndex
                          ? isLight ? 'w-2 bg-indigo-300 hover:bg-indigo-400' : 'w-2 bg-zinc-600 hover:bg-zinc-500'
                          : isLight ? 'w-2 bg-slate-300 hover:bg-slate-400' : 'w-2 bg-zinc-800 hover:bg-zinc-700'
                      }`}
                      title={`Go to step ${i + 1}`}
                    ></button>
                  ))}
                </div>
                <span className={`text-[10px] font-mono font-bold ${
                  isLight ? 'text-slate-500' : 'text-zinc-400'
                }`}>
                  {Math.round(((currentStepIndex + 1) / totalSteps) * 100)}%
                </span>
              </div>

              {/* Card Footer Navigation Actions */}
              <div className={`px-4 sm:px-5 py-3 border-t shrink-0 sticky bottom-0 z-20 flex items-center justify-between gap-3 shadow-md ${
                isLight ? 'bg-slate-50 border-slate-200' : 'bg-zinc-950 border-zinc-800'
              }`}>
                <button
                  onClick={closeTour}
                  className={`text-xs font-medium cursor-pointer py-1.5 px-2 rounded-lg transition-colors ${
                    isLight 
                      ? 'text-slate-500 hover:text-slate-800 hover:bg-slate-200' 
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
                  }`}
                >
                  Skip Tour
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={prevStep}
                    disabled={currentStepIndex === 0}
                    className={`py-2 px-3.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 cursor-pointer disabled:cursor-not-allowed border ${
                      isLight
                        ? 'bg-slate-200 hover:bg-slate-300 disabled:opacity-30 disabled:hover:bg-slate-200 text-slate-800 border-slate-300'
                        : 'bg-zinc-800 hover:bg-zinc-700 disabled:opacity-25 disabled:hover:bg-zinc-800 text-zinc-200 border-zinc-700/60'
                    }`}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Back
                  </button>

                  <button
                    onClick={nextStep}
                    className="py-2 px-4.5 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 !text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-indigo-500/25 flex items-center gap-1.5 cursor-pointer ring-2 ring-indigo-400/40 hover:ring-indigo-400 active:scale-98"
                  >
                    {currentStepIndex === totalSteps - 1 ? (
                      <>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-200" /> <span className="!text-white font-bold">Finish Tour</span>
                      </>
                    ) : (
                      <>
                        <span className="!text-white font-bold">Next</span> <ChevronRight className="w-3.5 h-3.5 text-white" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
